import { useSyncExternalStore } from 'react';

/**
 * Pre-release feature gating. A preview feature is hidden until enabled for
 * the browser: defaults to ON outside production builds and OFF in production,
 * with a per-browser override persisted from a `?preview=on|off` URL param on
 * the gated route (processed by CustomRoute). Registry of gateable features:
 */
export const PREVIEW_FEATURES = {
  purchasingInsights: 'purchasing-insights',
} as const;

export type PreviewFeature =
  (typeof PREVIEW_FEATURES)[keyof typeof PREVIEW_FEATURES];

const storageKey = (feature: PreviewFeature) => `me:preview:${feature}`;

const DEFAULT_ENABLED = import.meta.env.VITE_MODE !== 'production';

// Same-tab subscribers so nav items re-render when a toggle happens.
const listeners = new Set<() => void>();

export function isPreviewEnabled(feature: PreviewFeature): boolean {
  try {
    const stored = localStorage.getItem(storageKey(feature));
    if (stored === 'on') return true;
    if (stored === 'off') return false;
  } catch {
    // Storage unavailable (private mode etc.) — fall through to the default.
  }
  return DEFAULT_ENABLED;
}

export function setPreviewEnabled(
  feature: PreviewFeature,
  enabled: boolean
): void {
  try {
    localStorage.setItem(storageKey(feature), enabled ? 'on' : 'off');
  } catch {
    return;
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Reactive read of a preview feature's enabled state. */
export function usePreviewFeature(feature: PreviewFeature): boolean {
  return useSyncExternalStore(subscribe, () => isPreviewEnabled(feature));
}
